import { useCallback, useRef, useState } from 'react'

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'

import { getDownloadURL, upload } from 'lib/storage'

export default function ImageUploader({ value, path, error, onChange }) {
  const canvas = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  const elementSize = { width: 100, height: 100 }
  const imageSize = { width: 200, height: 200 }

  const rect = useCallback((w, h) => {
    if (h > w) {
      return {
        x: 0,
        y: (h - w) / 2,
        w: w,
        h: w,
      }
    } else {
      return {
        x: (w - h) / 2,
        y: 0,
        w: h,
        h: h,
      }
    }
  }, [])

  const submit = useCallback(
    async url => {
      if (!canvas) return
      setSubmitting(true)
      const image = new Image()
      const context = canvas.current.getContext('2d')
      image.src = url
      image.onload = async () => {
        const { x, y, w, h } = rect(image.width, image.height)
        const { width, height } = canvas.current
        context.drawImage(image, x, y, w, h, 0, 0, width, height)
        const dataURL = canvas.current.toDataURL('image/jpeg', 0.8)

        // Storage にアップロード
        await upload(path, dataURL)
        const url = await getDownloadURL(path)
        onChange(url)
        setSubmitting(false)
      }
    },
    [onChange, path, rect, canvas]
  )

  const handleChange = ({ target }) => {
    const reader = new FileReader()
    if (target.files.length === 0) return
    reader.readAsDataURL(target.files[0])
    reader.onload = e => submit(e.target.result)
  }

  return (
    <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
      <input
        id="image"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <label htmlFor="image">
        <ButtonBase component="span">
          <canvas
            id="canvas"
            ref={canvas}
            width={imageSize.width}
            height={imageSize.height}
            style={{ display: 'none' }}
          />
          {value ? (
            <img alt="在庫画像" src={value} style={{ ...elementSize, borderRadius: 4 }} />
          ) : (
            <Box
              sx={{
                ...elementSize,
                bgcolor: 'background.default',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography sx={{ mb: 0.5 }} variant="caption" component="p">
                    商品画像
                  </Typography>
                  <PhotoCameraIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                </>
              )}
            </Box>
          )}
        </ButtonBase>
      </label>
      {error && (
        <Typography color="error" variant="caption">
          {error.message}
        </Typography>
      )}
    </Box>
  )
}
